import { memo, useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { analyticsActions } from '@actions';
import { useLocations } from '@hooks';
import DoughnutRates from '@features/Analytics/Common/DoughnutRates';
import SentimentTrend from '@features/Analytics/Common/SentimentTrend';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import { AnalyticsContainer, HeaderWrapper } from '@features/Analytics/styles';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import FiltersPanel from './FiltersPanel';
import ReviewsChannelTrend from './ReviewsChannelTrend';
import ReviewsTable from './ReviewsTable';
import { FiltersIntegrationType } from '@constants/integrations';
import { GroupByFilter } from '@constants/analytics';
import { subtractYears } from '@helpers/dates';

const Reviews = memo(() => {
    const firstRender = useRef(true);

    const [filter, setFilter] = useState({
        locations: [],
        integrations: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
        grouping: GroupByFilter.Months,
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const language = useSelector(state => state.auth.user?.language);
    const { reviewsData, reviewsLoading } = useSelector(state => state.analytics);
    const dispatch = useDispatch();
    const { mapLocationsToFormValue } = useLocations();

    const availableLocations = useMemo(
        () =>
            locations.filter(
                location =>
                    location?.profiles?.length > 0 && location?.profiles.some(p => p.type in FiltersIntegrationType),
            ),
        [locations],
    );

    useEffect(() => {
        const integrations = [
            ...new Set(
                availableLocations
                    .map(location => location.profiles)
                    .flat()
                    .filter(p => p.type in FiltersIntegrationType)
                    .map(p => p.type),
            ),
        ];
        setFilter(prev => ({ ...prev, integrations, locations: mapLocationsToFormValue(availableLocations) }));
    }, [availableLocations, dispatch]);

    const onApplyFilters = () => {
        dispatch(
            analyticsActions.getAnalyticsReviews({
                locations: filter.locations.map(item => item.id),
                profileTypes: filter.integrations,
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                grouping: filter.grouping,
            }),
        );
    };

    useEffect(() => {
        if (filter.locations.length && filter.integrations.length) {
            if (firstRender.current) {
                onApplyFilters();
                firstRender.current = false;
            }
        }
    }, [filter, dispatch]);

    return (
        <AnalyticsWrapper>
            <HeaderWrapper>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    filter={filter}
                    setFilter={setFilter}
                    type="reviews"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                />
                <PrintButton />
            </HeaderWrapper>
            <AnalyticsContainer id="pl-content-analytics-reviews">
                <DoughnutRates data={reviewsData.overall} type="reviews" isLoading={reviewsLoading} />
                <SentimentTrend data={reviewsData.sentimentData} language={language} type="reviews" />
                <ReviewsChannelTrend data={reviewsData.channelData} language={language} />
                <ReviewsTable data={reviewsData.tableData} isLoading={reviewsLoading} filter={filter} />
            </AnalyticsContainer>
        </AnalyticsWrapper>
    );
});

Reviews.displayName = 'Reviews';

export default Reviews;
